import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { isChunkLoadError, handleChunkError } from '@/utils/chunkRetry';

// Only English translations loaded eagerly (fallback language — always available)
import commonEn from './en/common.json';
import createEn from './en/create.json';
import reviewEn from './en/review.json';
import onboardingEn from './en/onboarding.json';
import libraryEn from './en/library.json';
import settingsEn from './en/settings.json';
import homeEn from './en/home.json';
import foundingEn from './en/founding.json';
import statsEn from './en/stats.json';
import milestonesEn from './en/milestones.json';
import faqEn from './en/faq.json';
import landingEn from './en/landing.json';
import aboutEn from './en/about.json';
import tooltipsEn from './en/tooltips.json';
import authEn from './en/auth.json';
import tutorEn from './en/tutor.json';

// All other locales (de, es, ru, uk, fr, it, pt, ja) are loaded on demand
// via dynamic import() — Vite code-splits each locale into a separate chunk.
const NAMESPACES = [
  'common',
  'create',
  'review',
  'onboarding',
  'library',
  'settings',
  'home',
  'founding',
  'stats',
  'milestones',
  'faq',
  'landing',
  'about',
  'tooltips',
  'auth',
  'tutor',
] as const;
const localeLoaders = import.meta.glob('./{de,es,ru,uk,fr,it,pt,ja}/*.json');

i18n
  .use(initReactI18next)
  .use(
    resourcesToBackend(async (lng: string, ns: string) => {
      try {
        const loader = localeLoaders[`./${lng}/${ns}.json`];
        if (!loader) throw new Error(`Missing i18n namespace: ${lng}/${ns}`);
        return await loader();
      } catch (error) {
        if (isChunkLoadError(error)) handleChunkError();
        throw error;
      }
    }),
  )
  .init({
    resources: {
      en: {
        common: commonEn,
        create: createEn,
        review: reviewEn,
        onboarding: onboardingEn,
        library: libraryEn,
        settings: settingsEn,
        home: homeEn,
        founding: foundingEn,
        stats: statsEn,
        milestones: milestonesEn,
        faq: faqEn,
        landing: landingEn,
        about: aboutEn,
        tooltips: tooltipsEn,
        auth: authEn,
        tutor: tutorEn,
      },
    },
    lng: 'en',
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: [...NAMESPACES],
    partialBundledLanguages: true,
    interpolation: {
      escapeValue: false, // React already escapes
    },
  });

export default i18n;
